import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import * as yup from "yup";
import { useFormik } from "formik";
import FormDialog from "../../components/FormDialog";
import {
  Log,
  getNoPayoutDayDetails,
  updateNoPayoutDay,
  createNoPayoutDay,
} from "../../services/services";
import moment from "moment";
import CustomDatePicker from "../../components/form/CustomDatePicker";
import { NO_PAYOUT_DATE_DAYS, acquirerList } from "../../utils/constants";

const CreateDialog = ({ open, onClose, selectedId, initialData, onSuccess }) => {
  const [loaded, setLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const isEdit = !!selectedId;

  const {
    handleChange,
    handleSubmit,
    handleBlur,
    values,
    touched,
    errors,
    setValues,
    resetForm,
  } = useFormik({
    initialValues: {
      no_payout_date: "",
      no_payout_day: "",
      description: "",
      acquirer: "all",
    },
    validationSchema: yup.object().shape({
      no_payout_date: yup
        .string()
        .required("Required")
        .test("is-valid-date", "Invalid date format (YYYY-MM-DD)", (value) => {
          if (!value) return false;
          return moment(value, "YYYY-MM-DD", true).isValid();
        }),
      description: yup
        .string()
        .required("Required")
        .max(255, "Maximum 255 characters"),
    }),
    onSubmit: async (values) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setIsSubmitting(true);

      try {
        const submitValues = {
          no_payout_date: values.no_payout_date,
          no_payout_day: values.no_payout_day,
          description: values.description ? values.description.trim() : "",
          oxygen7_entity: "SGP",
          acquirer: values.acquirer || "all",
        };

        if (isEdit) {
          Log.info(`Click button [Submit] - Update No Payout Day by Id: ${selectedId}`, submitValues);
          await updateNoPayoutDay(selectedId, submitValues, onSuccess);
        } else {
          Log.info("Click button [Submit] - Create No Payout Day", submitValues);
          await createNoPayoutDay(submitValues, onSuccess);
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (values.no_payout_date && moment(values.no_payout_date, "YYYY-MM-DD", true).isValid()) {
      const dayNum = moment(values.no_payout_date).day();
      const dayIndex = dayNum === 0 ? 6 : dayNum - 1;
      setValues((prev) => ({ ...prev, no_payout_day: NO_PAYOUT_DATE_DAYS[dayIndex] }));
    }
  }, [values.no_payout_date, setValues]);

  useEffect(() => {
    if (open) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);

      if (isEdit && selectedId) {
        if (initialData) {
          setValues({
            no_payout_date: initialData.no_payout_date || "",
            no_payout_day: initialData.no_payout_day || "",
            description: initialData.description || "",
            oxygen7_entity: initialData.oxygen7_entity || "SGP",
            acquirer: initialData.acquirer || "all",
          });
          setLoaded(true);
        } else {
          setLoaded(false);
          getNoPayoutDayDetails(
            selectedId,
            (data) => {
              setValues({
                no_payout_date: data.no_payout_date || "",
                no_payout_day: data.no_payout_day || "",
                description: data.description || "",
                oxygen7_entity: data.oxygen7_entity || "SGP",
                acquirer: data.acquirer || "all",
              });
              setLoaded(true);
            },
            setLoaded
          );
        }
      } else {
        resetForm();
        setLoaded(true);
      }
    } else {
      setLoaded(false);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [open, selectedId, isEdit, initialData, setValues, resetForm]);

  const handleClose = () => {
    resetForm();
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    onClose();
  };

  const handleFormSubmit = React.useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (isSubmittingRef.current || isSubmitting) return false;
      handleSubmit(e);
      return false;
    },
    [isSubmitting, handleSubmit]
  );

  if (!open) return null;

  return loaded ? (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={isEdit ? "Edit No Payout Day" : "Create No Payout Day"}
      handleSubmit={handleFormSubmit}
      loading={isSubmitting}
      disabledSubmit={isSubmitting}
      maxWidth="sm"
      showActions={true}
    >
      <form onSubmit={handleFormSubmit} noValidate>
        <Grid container spacing={2} alignItems="center">
          <Grid item md={4} xs={12}>
            <Typography variant="subtitle1" align="right">
              Date *
            </Typography>
          </Grid>
          <Grid item md={8} xs={12}>
            <CustomDatePicker
              inputFormat="YYYY-MM-DD"
              disableFuture={false}
              value={values.no_payout_date ? moment(values.no_payout_date, "YYYY-MM-DD") : null}
              onChange={(newValue) => {
                const formattedDate = newValue ? moment(newValue).format("YYYY-MM-DD") : "";
                handleChange({ target: { name: "no_payout_date", value: formattedDate } });
              }}
              error={Boolean(touched.no_payout_date && errors.no_payout_date)}
              helperText={touched.no_payout_date && errors.no_payout_date}
              disabled={isSubmitting}
              margin="dense"
              views={["year", "month", "day"]}
              openTo="day"
            />
          </Grid>

          <Grid item md={4} xs={12}>
            <Typography variant="subtitle1" align="right">
              Day
            </Typography>
          </Grid>
          <Grid item md={8} xs={12}>
            <TextField
              name="no_payout_day"
              value={values.no_payout_day || ""}
              variant="outlined"
              fullWidth
              size="small"
              margin="dense"
              disabled
              placeholder="Auto-selected from date"
            />
          </Grid>

          <Grid item md={4} xs={12}>
            <Typography variant="subtitle1" align="right">
              Acquirer
            </Typography>
          </Grid>
          <Grid item md={8} xs={12}>
            <FormControl fullWidth size="small" margin="dense" disabled={isSubmitting}>
              <Select
                name="acquirer"
                value={values.acquirer || "all"}
                onChange={handleChange}
                onBlur={handleBlur}
                displayEmpty
                IconComponent={KeyboardArrowDown}
              >
                <MenuItem value="all">All</MenuItem>
                {acquirerList.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={4} xs={12}>
            <Typography variant="subtitle1" align="right">
              Description *
            </Typography>
          </Grid>
          <Grid item md={8} xs={12}>
            <TextField
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              fullWidth
              size="small"
              margin="dense"
              placeholder=""
              error={Boolean(touched.description && errors.description)}
              helperText={touched.description && errors.description}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </form>
    </FormDialog>
  ) : (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

export default CreateDialog;
